<template>
  <div id="cover-page" data-cy="report-cover-page">
    <img
      :src="imagePair.header"
      alt="spyglass logo"
      class="header-img"
      data-cy="header-image"
    />
    <div class="red-band align-self-end"></div>
    <div class="flex" data-cy="sizing-report-container">
      <span class="sizing-report">
        SIZING
        <span>REPORT</span>
      </span>
    </div>

    <div
      v-for="field in fields"
      :key="field.label"
      class="field-row"
      :data-cy="`field-${field.label.toLowerCase().replace(/\s/g, '-')}`"
    >
      <span class="icon-span mr-5"><icon :icon="field.icon" /></span>
      <div>
        <p class="field-name">{{ field.label }}</p>
        <p class="field-value">{{ field.value }}</p>
      </div>
    </div>

    <p class="mt-auto prepared-by" data-cy="prepared-by">
      Prepared by {{ user.name }}
    </p>
    <img
      :src="imagePair.footer"
      :class="`report-cover-footer ${imagePair.footerClass}`"
      alt="summit footer"
      data-cy="summit-footer-image"
    />
  </div>
</template>

<script setup lang="ts">
import useUserInfo from "@/composables/auth/useUserInfo";
import type { Sizing } from "@/composables/forms/formTypes";
import useReportCoverImages from "@/composables/ui/useReportCoverImages";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { computed } from "vue";

const props = defineProps<{ sizing?: Sizing | null; dateGenerated: string }>();
const user = useUserInfo();

interface Field {
  icon: [string, string];
  label: string;
  value: string;
}

const isHPS = props.sizing?.pumping_type === Sizing_Pumping_Type_Enum.Hps;

const imagePair = useReportCoverImages(isHPS);

const fields = computed<Field[]>(() => [
  {
    icon: ["fas", "user"],
    label: "Customer",
    value: props.sizing?.customer?.customer_name || "TBD"
  },
  {
    icon: ["fas", "droplet"],
    label: isHPS ? "Location" : "Well",
    value: isHPS
      ? props.sizing?.hps_location || "TBD"
      : props.sizing?.well?.well_name || "TBD"
  },
  {
    icon: ["fas", "pen-ruler"],
    label: "Sizing",
    value: props.sizing?.title || "No Sizing Title"
  },
  {
    icon: ["fas", "calendar"],
    label: "Date Generated",
    value: props.dateGenerated
  }
]);
</script>

<style lang="scss" scoped>
#cover-page {
  width: 100%;
  min-height: 11in;
  display: flex;
  flex-direction: column;
  position: relative;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  .header-img {
    width: 100%;
    object-fit: contain;
  }

  .sizing-report {
    font-size: 36px;
    letter-spacing: 4px;
    padding: 20px 36px;
    margin: -44px 0 0 -2px;
    background-color: white;
    span {
      font-weight: 200;
    }
  }

  .field-row {
    display: flex;
    align-items: center;
    margin: 20px 0 0 30px;

    .icon-span {
      background: linear-gradient(0deg, #ed213a -62.5%, #93291e 100%);
      border-radius: 50%;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      svg {
        color: white;
      }
    }

    .field-name {
      font-size: 14px;
      font-weight: 300;
      color: var(--gray-800);
      margin: 0;
    }
    .field-value {
      font-size: 20px;
      margin: 0;
    }
  }

  .prepared-by {
    font-size: 14px;
    padding: 14px 35px;
    color: var(--gray-800);
  }

  .red-band {
    height: 9px;
    background: linear-gradient(90deg, #ed213a 5.27%, #93291e 100%);
    width: 60%;
    margin-top: -1px;
  }

  .report-cover-footer {
    position: absolute;
    right: 0;
    bottom: 0px;
    width: 320px;
    &.summit-footer {
      right: 0.5px;
      bottom: 10px;
    }
  }
}

@media print {
  @page {
    height: 100vh;
  }

  #cover-page {
    page-break-after: always;
    page-break-inside: avoid;
    height: 100vh;
    min-height: 100vh;
  }
}
</style>
